import {Button} from '@nlabs/gothamjs/components';
import {ArrowLeft, ArrowUpRight, BookOpen, Check, ChevronDown, Download, Film, Pencil, Plus} from '@nlabs/gothamjs/icons';
import {PLAN_LIMITS} from '@reactorbox/shared';
import {useState} from 'react';

import {primaryButton, secondaryButton} from '../../theme/classes.js';
import {CreativeAssistant} from '../CreativeAssistant/CreativeAssistant.js';
import {ElementsPanel} from '../ElementsPanel/ElementsPanel.js';
import {PreviewFrame} from '../PreviewFrame/PreviewFrame.js';
import {ShotCard} from '../ShotCard/ShotCard.js';
import {ShotEditor} from '../ShotEditor/ShotEditor.js';

import type {Project, Workspace} from '@reactorbox/shared';
import type {ReactElement} from 'react';
import type {WorkspaceActions} from '../../state/workspaceActions.js';

interface StudioProps {
  readonly actions: WorkspaceActions;
  readonly busy: boolean;
  readonly onBack: () => void;
  readonly onCharacters: () => void;
  readonly onEdit: () => void;
  readonly project: Project;
  readonly workspace: Workspace;
}

export const Studio = ({
  actions, busy, onBack, onCharacters, onEdit, project, workspace
}: StudioProps): ReactElement => {
  const [selectedId, setSelectedId] = useState<string | undefined>(project.shots[0]?.id);
  const [adding, setAdding] = useState(false);
  const selected = project.shots.find((shot) => shot.id === selectedId);
  const selectedIndex = Math.max(project.shots.findIndex((shot) => shot.id === selectedId), 0);
  const selectedAssets = workspace.assets.filter((asset) => asset.shotId === selected?.id);
  const selectedAsset = selectedAssets.find((asset) => asset.id === selected?.approvedAssetId) || selectedAssets[0];
  const approved = project.shots.filter((shot) => shot.approvedAssetId).length;
  const showInspector = Boolean(selected || adding);
  const select = (id: string): void => {
    setSelectedId(id);
    setAdding(false);
  };
  const addShot = (): void => {
    setAdding(true);
    setSelectedId(undefined);
  };
  const closeInspector = (): void => {
    setAdding(false);
    setSelectedId(undefined);
  };
  let mediaLabel = 'Illustrative artwork · not generated';
  if(selectedAsset) {
    mediaLabel = selectedAsset.isDemo ? 'Illustrative preview · local simulation' : 'Generated media';
  }

  return (
    <div className="mx-auto w-full max-w-[1800px] pb-6 text-white">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            aria-label="All projects"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-white/10 text-white/50 transition hover:bg-white/10 hover:text-white"
            onClick={onBack}
            type="button"
          >
            <ArrowLeft size={15} />
          </button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-[24px] font-medium tracking-[-0.035em] text-violet-200 sm:text-[28px]">{project.name}</h1>
              <span className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-white/50">
                {workspace.plan !== 'free' && workspace.mode === 'demo' ? 'Preview project' : 'Story project'}
              </span>
            </div>
            <p className="mt-1 max-w-xl truncate text-[12px] leading-relaxed text-white/45" title={project.brief}>
              {project.brief || 'Build your story, one shot at a time.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button className={secondaryButton} icon={<Pencil size={13} />} onClick={onEdit} variant="outline">
            Project details
          </Button>
          {(!workspace.plan || PLAN_LIMITS[workspace.plan].export) && <a className={`${secondaryButton} inline-flex items-center justify-center gap-2`} download href={`/api/projects/${project.id}/export`}>
            <Download size={13} />Export shot list
          </a>}
        </div>
      </header>
      <CreativeAssistant actions={actions} busy={busy} project={project} shotId={selected?.id} workspace={workspace} />
      <ElementsPanel actions={actions} busy={busy} workspace={workspace} />
      <div className={`grid items-start gap-5 ${showInspector ? 'xl:grid-cols-[minmax(0,1fr)_330px] 2xl:grid-cols-[minmax(0,1fr)_356px]' : 'grid-cols-1'}`}>
        <section aria-labelledby="storyboard-title" className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-[16px] font-medium tracking-tight text-violet-200" id="storyboard-title">Storyboard</h2>
              <span className="text-[11px] text-white/50">{project.shots.length} shots</span>
            </div>
            <Button className={primaryButton} icon={<Plus size={14} />} onClick={addShot}>Add shot</Button>
          </div>
          <div className="relative isolate overflow-hidden rounded-[28px] border border-white/10 bg-[#19171d] sm:rounded-[32px]">
            <div className="relative flex h-[360px] w-full items-center justify-center overflow-hidden sm:h-[420px]">
              {selectedAsset?.mediaUrl && selectedAsset.kind === 'video' && !selectedAsset.isDemo && (
                <video
                  aria-label={`${selected?.title || 'Selected shot'} video`}
                  className="size-full bg-black object-contain"
                  controls
                  poster={selectedAsset.posterUrl}
                  preload="metadata"
                  src={selectedAsset.mediaUrl}
                />
              )}
              {selectedAsset?.mediaUrl && selectedAsset.kind === 'image' && (
                <img alt={selected?.title || 'Selected shot'} className="size-full object-contain" src={selectedAsset.mediaUrl} />
              )}
              {(!selectedAsset?.mediaUrl || (selectedAsset.kind === 'video' && selectedAsset.isDemo)) && (
                <>
                  <div aria-hidden="true" className="pointer-events-none absolute -inset-10 scale-110 opacity-25 blur-3xl">
                    <PreviewFrame artwork="project" className="size-full" index={selectedIndex} />
                  </div>
                  <div className="relative aspect-video max-h-[calc(100%_-_24px)] w-[calc(100%_-_24px)] max-w-176 overflow-hidden rounded-[20px] shadow-2xl">
                    <PreviewFrame
                      artwork="project"
                      className="size-full"
                      index={selectedIndex}
                      label={`${selected?.title || project.name} illustrative artwork — not generated output`}
                    />
                  </div>
                </>
              )}
            </div>
            {(!selectedAsset?.mediaUrl || selectedAsset.kind !== 'video' || selectedAsset.isDemo) && (
              <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/35 via-transparent to-black/10" />
            )}
            <div className="pointer-events-none absolute top-4 left-4 flex flex-wrap items-center gap-2 sm:top-5 sm:left-5">
              <span className="rounded-full border border-white/15 bg-black/25 px-3 py-1.5 text-[11px] text-white/90 backdrop-blur-xl">
                {mediaLabel}
              </span>
              {selected?.approvedAssetId && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#d0f4d8]/90 px-2.5 py-1.5 text-[11px] text-[#284332]">
                  <Check size={10} />Approved take
                </span>
              )}
            </div>
            {(!selectedAsset?.mediaUrl || selectedAsset.kind !== 'video' || selectedAsset.isDemo) && (
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 sm:p-7">
                <div className="min-w-0">
                  <span className="text-[11px] tracking-[0.18em] text-white/45">
                    {selected ? `SHOT ${String(selectedIndex + 1).padStart(2, '0')}` : 'YOUR NEXT SCENE'}
                  </span>
                  <h3 className="mt-1.5 truncate text-[21px] font-medium tracking-tight sm:text-[28px]">
                    {selected?.title || 'A new scene starts here.'}
                  </h3>
                  <p className="mt-1 text-[11px] text-white/45">
                    {selected ? `${selected.duration}s · ${selected.resolution}` : 'Add a shot to begin your storyboard.'}
                  </p>
                </div>
                {selectedAsset?.mediaUrl && (
                  <a
                    aria-label="Download selected take"
                    className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-xl transition hover:bg-white/20"
                    download
                    href={selectedAsset.mediaUrl}
                  >
                    <Download size={16} />
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-1 text-[11px] text-white/50">
            <span className="inline-flex items-center gap-2">
              <Film size={12} />{project.shots.reduce((total, shot) => total + shot.duration, 0)} seconds planned
            </span>
            <div className="flex items-center gap-4">
              <span>{approved} approved</span>
              <button className="inline-flex items-center gap-1 text-sky-200 transition hover:text-sky-100" onClick={onCharacters} type="button">
                {project.characters.length} characters <ArrowUpRight size={12} />
              </button>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between px-1">
            <h3 className="text-[12px] font-medium text-white/65">Your shots</h3>
            <span className="text-[11px] text-white/45">Select a shot to edit</span>
          </div>
          <div aria-label="Storyboard shots" className="-mx-1 mt-2 flex gap-3 overflow-x-auto px-1 pt-1 pb-3">
            {project.shots.map((shot, index) => (
              <ShotCard
                assets={workspace.assets.filter((asset) => asset.shotId === shot.id)}
                index={index}
                jobs={workspace.jobs.filter((job) => job.shotId === shot.id)}
                key={shot.id}
                onSelect={() => select(shot.id)}
                selected={selectedId === shot.id}
                shot={shot}
              />
            ))}
            <button
              className="flex min-h-[170px] w-[145px] shrink-0 flex-col items-center justify-center gap-2 rounded-[20px] border border-dashed border-white/15 text-white/35 transition hover:border-white/35 hover:bg-white/5 hover:text-white/65"
              onClick={addShot}
              type="button"
            >
              <Plus size={20} />
              <strong className="mt-1 text-[11px] font-medium">Add the next frame</strong>
              <span className="text-[11px] text-white/45">Continue your story</span>
            </button>
          </div>
          {project.style && (
            <details className="mt-4 rounded-2xl border border-white/10 bg-white/[0.025] px-4 py-3.5">
              <summary className="flex cursor-pointer list-none items-center gap-2 text-[11px] text-white/50">
                <BookOpen size={13} />Visual direction <ChevronDown className="ml-auto" size={13} />
              </summary>
              <p className="mt-3 text-[11px] leading-7 text-white/40">{project.style}</p>
            </details>
          )}
        </section>
        {showInspector && (
          <ShotEditor
            actions={actions}
            busy={busy}
            key={adding ? `new-${project.shots.length}` : `${selected!.id}-${selected!.title}-${selected!.description}-${selected!.duration}-${selected!.resolution}-${selected!.referenceUrl}-${selected!.characterIds.join()}`}
            onClose={closeInspector}
            project={project}
            shot={selected}
            workspace={workspace}
          />
        )}
      </div>
    </div>
  );
};
